// @flow
import BaseCollection from "../base/BaseCollection";
import ActionModel from "./ActionModel";
import type { ModelOptions } from "../types";

/**
 * Collection of actions
 */
export default class ActionCollection extends BaseCollection<ActionModel> {
  constructor(
    actions?: Object,
    actionsContributions?: Object,
    modelOptions?: ModelOptions,
  ) {
    super();

    // no actions gives an empty collection
    if (!Array.isArray(actions)) {
      this.collection = [];
    } else if (Array.isArray(actionsContributions)) {
      const actionModels = [];
      for (const actionData of actions) {
        const actionContribution = actionsContributions.find(
          (contribution) =>
            contribution.name === actionData.name &&
            contribution.type !== "service-operation",
        );

        if (actionContribution) {
          actionModels.push(
            new ActionModel(actionData, actionContribution, modelOptions),
          );
        }
      }

      this.collection = actionModels;
    }
  }

  /**
   * Retrieve action by key
   */
  getActionByKey(key: string): ActionModel | null {
    const newCollection = new ActionCollection();

    newCollection.collection = this.filter((action) => action.key === key);

    return newCollection.first;
  }

  /**
   * Retrieve actions by type
   */
  getActionsByType(type: string | Array<string>): ActionCollection {
    const newCollection = new ActionCollection();

    newCollection.collection = this.filter((action) =>
      Array.isArray(type) ? type.includes(action.type) : action.type === type,
    );

    return newCollection;
  }

  /**
   * Retrieve actions including a layout hint
   */
  getActionsByLayoutHint(...hints: Array<string>): ActionCollection {
    const newCollection = new ActionCollection();

    newCollection.collection = this.filter((action) =>
      action.layouthint.has(...hints),
    );

    return newCollection;
  }

  /**
   * Indicates if an action with layout hint exists
   */
  hasActionsByLayoutHint(...hints: Array<string>): boolean {
    return this.getActionsByLayoutHint(...hints).length > 0;
  }

  /**
   * Use as path regex for react router routes
   */
  get routePath(): string {
    const enabledRoutes = this.collection.filter(
      (action) => !action.isDisabled,
    );

    if (enabledRoutes.length === 0) {
      return "__NON_EXISTING_ROUTE__";
    }

    const path = enabledRoutes.map((action) => action.selfhref.path).join("|");

    return this.length > 1 ? `(${path})` : path;
  }
}
